import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Box,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import useStyles from './styles';
import { parseCompleteDate, getDuration, shouldHighlightDuration } from '../../../utils/date';

const DetailResponse = ({ responseDetail }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { taskResponseId } = useParams();

  const generalDetailsColumns = [
    {
      key: 'surveyResponseId',
      label: t('detailResponse.tableHeaders.surveyResponseId'),
    },
    {
      key: 'locationName',
      label: t('detailResponse.tableHeaders.locationName'),
    },
    { key: 'taskName', label: t('detailResponse.tableHeaders.taskName') },
    { key: 'type', label: t('detailResponse.tableHeaders.type') },
    { key: 'category', label: t('detailResponse.tableHeaders.category') },
    { key: 'deleted', label: t('detailResponse.tableHeaders.status') },
  ];

  const measurementColumns = [
    { key: 'startedAt', label: t('detailResponse.tableHeaders.startedAt') },
    { key: 'finishedAt', label: t('detailResponse.tableHeaders.finishedAt') },
    {
      key: 'measurementTime',
      label: t('detailResponse.tableHeaders.measurementTime'),
    },
    { key: 'createdAt', label: t('detailResponse.tableHeaders.createdAt') },
  ];

  const userDataColumns = [
    { key: 'userName', label: t('detailResponse.tableHeaders.userName') },
    { key: 'userEmail', label: t('detailResponse.tableHeaders.userEmail') },
  ];

  /**
   * Parsea los valores
   * @param {*} value Valor a parsear
   * @returns Valor parseado
   */
  const parseValue = (key) => {
    const value = responseDetail[key];

    if (key === 'measurementTime') {
      return (
        getDuration(responseDetail.startedAt, responseDetail.finishedAt) || '-'
      );
    }

    if (value === null || value === undefined) return '-';

    if (key === 'locationName' || key === 'taskName') {
      const id = key === 'locationName'
        ? responseDetail.locationId
        : responseDetail.taskId;
      return `${value} (ID ${id ?? '-'})`;
    }

    if (key === 'deleted') {
      if (value) return t('status.deleted');
      if (responseDetail.reviewStatus === 'rejected') return t('qa.status.rejected');
      return t('status.active');
    }

    if (key === 'startedAt' || key === 'finishedAt' || key === 'createdAt' || key === 'startedAtReal') {
      return parseCompleteDate(value);
    }

    return value;
  };

  const highlightMeasurementTime = shouldHighlightDuration(
    responseDetail.startedAt,
    responseDetail.finishedAt,
    responseDetail.label,
  );

  const renderTable = (columns) => (
    <TableContainer component={Paper}>
      <Table className={classes.table}>
        <TableBody>
          {columns.map((item) => (
            <TableRow key={item.key}>
              <TableCell component="th" scope="row">
                <Typography className={classes.semiBold}>
                  {item.label}
                </Typography>
              </TableCell>
              <TableCell
                align="right"
                className={
                  item.key === 'measurementTime' && highlightMeasurementTime
                    ? classes.highlightMeasurementTime
                    : undefined
                }
              >
                {item.key === 'taskName' && responseDetail.taskId ? (
                  <Link
                    to={`/tasks/${responseDetail.taskId}`}
                    className={classes.taskLink}
                  >
                    {parseValue(item.key)}
                  </Link>
                ) : (
                  parseValue(item.key)
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <Typography variant="h1" className={classes.title} gutterBottom>
          {t('detailResponse.title')}
        </Typography>
        <Box ml={2}>
          <IconButton
            component={Link}
            to={{
              pathname: `${taskResponseId}/edit`,
              state: { from: `/responses/${taskResponseId}` },
            }}
          >
            <EditIcon titleAccess="Editar" />
          </IconButton>
        </Box>
      </Box>
      <Box className={classes.tablesContainer}>
        <Box className={classes.tableWrapper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t('detailResponse.sections.general')}
          </Typography>
          {renderTable(generalDetailsColumns)}
        </Box>
        <Box className={classes.tableWrapper}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {t('detailResponse.sections.measurement')}
          </Typography>
          {renderTable(measurementColumns)}
          <Box mt={2}>
            <Typography variant="h6" className={classes.sectionTitle}>
              {t('detailResponse.sections.userData')}
            </Typography>
            {renderTable(userDataColumns)}
          </Box>
        </Box>
      </Box>
    </>
  );
};

DetailResponse.propTypes = {
  responseDetail: PropTypes.shape({
    surveyResponseId: PropTypes.number,
    locationName: PropTypes.string,
    locationId: PropTypes.number,
    taskName: PropTypes.string,
    taskId: PropTypes.number,
    type: PropTypes.string,
    category: PropTypes.string,
    userName: PropTypes.string,
    userEmail: PropTypes.string,
    startedAt: PropTypes.string,
    finishedAt: PropTypes.string,
    createdAt: PropTypes.string,
    startedAtReal: PropTypes.string,
    deleted: PropTypes.bool,
    reviewStatus: PropTypes.string,
  }),
};

DetailResponse.defaultProps = {
  responseDetail: {
    surveyResponseId: null,
    locationName: null,
    locationId: null,
    taskName: null,
    taskId: null,
    type: null,
    category: null,
    userName: null,
    userEmail: null,
    startedAt: null,
    finishedAt: null,
    createdAt: null,
    startedAtReal: null,
    deleted: null,
    reviewStatus: null,
  },
};

export default DetailResponse;
